// src/components/Logs/InstituicaoLogsTable.js

import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TablePagination
} from '@mui/material';
import { Visibility as VisibilityIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Importando logService e NotificacaoContext
import logService from '../../services/logService'; // Ajuste o caminho conforme a localização do logService
import { NotificacaoContext } from '../../contexts/NotificacaoContext'; // Ajuste o caminho conforme a localização do NotificacaoContext

// Lista de entidades do dashboard
const ENTIDADES_DASHBOARD = [
  { value: '', label: 'Todas' },
  { value: 'ESTUDANTE', label: 'Estudantes' },
  { value: 'CAMPANHA', label: 'Campanhas' },
  { value: 'CURSO', label: 'Cursos' },
  { value: 'CLASSE', label: 'Classes' },
  { value: 'PLANOS-PAGAMENTO', label: 'Planos de Pagamento' },
  { value: 'PRE-INSCRICAO', label: 'Pré-Inscrições' },
  { value: 'SLIDERS', label: 'Sliders' },
  { value: 'DESTAQUES', label: 'Destaques' },
  { value: 'NOTIFICACOES', label: 'Notificações' },
  { value: 'DASHBOARD', label: 'Dashboard' },
  { value: 'RELATORIOS', label: 'Relatórios' },
  { value: 'DISCIPLINA', label: 'Disciplinas' },
  { value: 'PERIODO', label: 'Períodos' }
];

// Função para formatar nome da entidade
const formatarEntidade = (entidade) => {
  if (!entidade) return '-';
  const entidadeBase = entidade.split('?')[0].toUpperCase();
  return ENTIDADES_DASHBOARD.find(e => e.value === entidadeBase)?.label || entidade;
};

const InstituicaoLogsTable = ({ 
    logs, 
    loading, 
    error, 
    onViewDetails, 
    canExport,
    currentFilters, 
    instituicaoId   
  }) => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  // Estados de paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Acessando o contexto de notificações
  const notificacaoContext = useContext(NotificacaoContext);

  // Handlers para paginação
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Função para exportar logs
  const handleExportLogs = async () => {
    try {
      // Verificação explícita do instituicaoId
      if (!instituicaoId) {
        notificacaoContext?.adicionarNotificacao(
          'ID da instituição não disponível para exportação',
          'error'
        );
        return;
      }

      await logService.exportLogsInstituicao(instituicaoId, currentFilters || {});
      
      notificacaoContext?.adicionarNotificacao(
        'Logs exportados com sucesso',
        'success'
      );
    } catch (error) {
      console.error('Erro ao exportar logs:', error);
      notificacaoContext?.adicionarNotificacao(
        `Erro ao exportar logs: ${error.message || 'Erro desconhecido'}`,
        'error'
      );
    }
  };

  const handleOpenDetails = (log) => {
    setSelectedLog(log);
    setOpenDialog(true);
    if (onViewDetails) {
      onViewDetails(log);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLog(null);
  };

  const getStatusColor = (status) => {
    if (status < 300) return 'success';
    if (status < 400) return 'warning';
    return 'error';
  };

  const getActionColor = (acao) => {
    switch (acao?.toUpperCase()) {
      case 'CRIAR': return 'success';
      case 'ATUALIZAR': return 'primary';
      case 'EXCLUIR': return 'error';
      default: return 'default';
    }
  };

  // Função para formatar a ação
  const formatarAcao = (acao) => {
    if (!acao) return '-';
    return acao.charAt(0).toUpperCase() + acao.slice(1).toLowerCase();
  };

  // Função para formatar a data
  const formatarData = (data) => {
    if (!data) return '-';
    try {
      const dataObj = new Date(data);
      return format(dataObj, "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch (error) {
      return data;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!logs?.length) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="textSecondary">
          Nenhum log encontrado
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Adicionar botão de exportação */}
      {canExport && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportLogs}
            disabled={!instituicaoId}
          >
            Exportar Logs
          </Button>
          {!instituicaoId && (
            <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
              ID da instituição não disponível
            </Typography>
          )}
        </Box>
      )}

      <Table size="small" aria-label="tabela de logs">
        <TableHead>
          <TableRow>
            <TableCell><Typography variant="subtitle2">Data/Hora</Typography></TableCell>
            <TableCell><Typography variant="subtitle2">Usuário</Typography></TableCell>
            <TableCell><Typography variant="subtitle2">Ação</Typography></TableCell>
            <TableCell><Typography variant="subtitle2">Recurso</Typography></TableCell>
            <TableCell><Typography variant="subtitle2">Status</Typography></TableCell>
            <TableCell align="right"><Typography variant="subtitle2">Ações</Typography></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {logs
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>
                  {formatarData(log.createdAt)}
                </TableCell>
                <TableCell>{log.usuario?.nome || 'Sistema'}</TableCell>
                <TableCell>
                  <Chip 
                    label={formatarAcao(log.acao)}
                    color={getActionColor(log.acao)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatarEntidade(log.entidade)}</TableCell>
                <TableCell>
                  <Chip
                    label={log.status}
                    color={getStatusColor(log.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDetails(log)}
                    color="primary"
                  >
                    <VisibilityIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      {/* Componente de Paginação */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={logs.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count}`
        }
      />

      {/* Dialog para detalhes do log */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalhes do Log
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data/Hora
                </Typography>
                <Typography variant="body1">
                  {formatarData(selectedLog.createdAt)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Usuário
                </Typography>
                <Typography variant="body1">
                  {selectedLog.usuario?.nome || 'Sistema'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Ação
                </Typography>
                <Chip 
                  label={formatarAcao(selectedLog.acao)}
                  color={getActionColor(selectedLog.acao)}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Entidade
                </Typography>
                <Typography variant="body1">
                  {formatarEntidade(selectedLog.entidade)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedLog.status}
                  color={getStatusColor(selectedLog.status)}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  IP
                </Typography>
                <Typography variant="body1">
                  {selectedLog.ip || 'N/A'}
                </Typography>
              </Grid>

              {selectedLog.descricao && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Descrição
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.descricao}
                  </Typography>
                </Grid>
              )}

              {selectedLog.metadados && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Metadados
                  </Typography>
                  <Box sx={{ 
                    maxHeight: '200px', 
                    overflow: 'auto', 
                    bgcolor: 'grey.50',
                    p: 1,
                    borderRadius: 1
                  }}>
                    <pre style={{ margin: 0 }}>
                      {JSON.stringify(selectedLog.metadados, null, 2)}
                    </pre>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

InstituicaoLogsTable.propTypes = {
    logs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      createdAt: PropTypes.string.isRequired,
      usuario: PropTypes.shape({
        nome: PropTypes.string,
        email: PropTypes.string
      }),
      acao: PropTypes.string.isRequired,
      entidade: PropTypes.string.isRequired,
      status: PropTypes.number.isRequired,
      ip: PropTypes.string,
      descricao: PropTypes.string,
      metadados: PropTypes.object
    })),
    loading: PropTypes.bool,
    error: PropTypes.string,
    onViewDetails: PropTypes.func.isRequired,
    canExport: PropTypes.bool,
    // Adicionando as novas props
    currentFilters: PropTypes.shape({
      acao: PropTypes.string,
      entidade: PropTypes.string,
      status: PropTypes.string,
      dataInicio: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.oneOf([null])
      ]),
      dataFim: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
        PropTypes.oneOf([null])
      ])
    }),
    instituicaoId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]).isRequired // Marcado como obrigatório
  };
  
InstituicaoLogsTable.defaultProps = {
  logs: [],
  loading: false,
  error: null,
  canExport: false,
  currentFilters: {}
  // Removido instituicaoId dos defaultProps já que é obrigatório
};

export default InstituicaoLogsTable;
